import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import Loader from "../components/loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaBrain,
  FaComments,
  FaTasks,
  FaCheck,
  FaDownload,
  FaListAlt,
  FaChartBar,
  FaIdBadge,
  FaClipboardCheck,
  FaStar,
  FaCrown,
  FaCommentDots,
  FaChartPie,
  FaFileAlt,
} from "react-icons/fa";
import "./TriplePaymentGateway.css";

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://backend-ewrn.onrender.com";

const isMobileDevice = () => window.matchMedia("(max-width: 768px)").matches;

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PLAN_AMOUNTS = {
  Pro: 199,
  Premium: 299,
};

export default function TriplePaymentGateway({ setToggle, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [payLoading, setPayLoading] = useState(false);
  //const [hasPaid10, setHasPaid10] = useState(() => localStorage.getItem("ideaValidationPaid10") === "true");
  const [activePlan, setActivePlan] = useState(null);
  const [contactNum, setContactNum] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animatedDigits, setAnimatedDigits] = useState(() => ({
    Pro: [0, Math.floor(Math.random() * 5) + 2, Math.floor(Math.random() * 6) + 2],
    Premium: [0, Math.floor(Math.random() * 5) + 2, Math.floor(Math.random() * 6) + 2],
  }));

  const recordId = localStorage.getItem("ideaValidationPaymentRecordId");

  const reportId = localStorage.getItem("payingReportId");
  const reportType = localStorage.getItem("payingReportType");

  const reportsPath = "reports";
  const mailPath = "idea-validation";

  useEffect(() => {
    if (!reportId || !reportType) {
      console.warn("Missing report details, redirecting...");
      navigate(`/${reportsPath}`);
    }
  }, [reportId, reportType, navigate, reportsPath]);


  useEffect(() => {
    if (localStorage.getItem("proButtonActive") === "true") setActivePlan("Pro");
    else if (localStorage.getItem("premiumButtonActive") === "true") setActivePlan("Premium");
    else setActivePlan(null);
  }, []);

  useEffect(() => {
    if (!activePlan) return;

    const target = activePlan === "Pro" ? [1, 9, 9] : [2, 9, 9];
    let current = [...animatedDigits[activePlan]];

    const interval = setInterval(() => {
      current = current.map((digit, i) => {
        if (digit < target[i]) return digit + 1;
        return digit;
      });

      setAnimatedDigits((prev) => ({
        ...prev,
        [activePlan]: [...current],
      }));

      if (current[0] === target[0] && current[1] === target[1] && current[2] === target[2]) {
        clearInterval(interval);
      }
    }, 80);

    return () => clearInterval(interval);
  }, [activePlan, animatedDigits]);

  useEffect(() => {
    setContactNum(user?.mobile || null);
  }, [user]);

  const showToast = (message, type = "info") => {
    const options = {
      className: "triple-payment-gateway-toast",
      autoClose: 2000,
    };

    if (type === "success") toast.success(message, options);
    else if (type === "error") toast.error(message, options);
    else toast.info(message, options);
  };

  const sendSelectedReport = async (targetReportId, targetUserId, invoiceData) => {
    if (!targetReportId || !targetUserId) {
      console.error("Missing ID for sending report:", { targetReportId, targetUserId });
      showToast("Error: Missing User or Report ID.", "error");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${BACKEND_BASE_URL}/${mailPath}/send-latest-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userId: targetUserId,
          reportId: targetReportId,
          reportType: reportType,
          invoiceData: invoiceData,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast("Payment Success! Report sent to your email successfully!", "success");
        const isMobile = isMobileDevice();
        const path = isMobile ? `/mobile/reports` : `/reports`;
        navigate(path);
      } else {
        console.warn("Report send failed:", data?.message);
        showToast("Payment Success! (Report email pending)", "success");
        const isMobile = isMobileDevice();
        const path = isMobile ? `/mobile/reports` : `/reports`;
        navigate(path);
      }
    } catch (err) {
      console.error("Send report error:", err);
      showToast("Payment Success! (Server error sending email)", "success");
      const isMobile = isMobileDevice();
      const path = isMobile ? `/mobile/reports` : `/reports`;
      navigate(path);
    } finally {
      setLoading(false);
    }
  };
  const handleInitialPayment = async () => {
    if (!recordId) {
      alert("Unable to proceed: missing report reference.");
      return;
    }

    setPayLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        alert("Unable to load payment gateway. Please try again.");
        return;
      }

      const res = await fetch(`${BACKEND_BASE_URL}/idea-validation/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      const orderData = await res.json();
      if (!orderData?.success) {
        alert("Failed to create payment order. Please try again.");
        return;
      }

      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKey) {
        alert("Payment configuration error. Please try again later.");
        return;
      }

      const options = {
        key: razorpayKey,
        amount: orderData.amount,
        currency: "INR",
        name: "Marsmate AI",
        description: "Idea Validation Scorecard",
        order_id: orderData.order_id,
        prefill: {
          name: user?.name || "User",
          email: user?.email || "",
        },
        theme: { color: "#F45900" },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${BACKEND_BASE_URL}/idea-validation/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                recordId,
              }),
              credentials: "include",
            });

            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              localStorage.setItem("ideaValidationPaid10", "true");
              localStorage.setItem("payingReportId", recordId);
              localStorage.setItem("payingReportType", "A");
              localStorage.setItem("scorecardAccess", "true");
              localStorage.setItem("scorecardtoken", "true");
              navigate(`/scorecard`);
            } else {
              alert("Payment verification failed.");
            }
          } catch (err) {
            console.error(err);
            alert("Error verifying payment.");
          }
        },
        modal: {
          ondismiss: () => {},
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        alert("Payment Failed. Reason: " + response.error.description);
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Error initiating payment.");
    } finally {
      setPayLoading(false);
    }
  };

  const startReportPayment = async (plan) => {
    setToggle(false);
    onClose();

    if (!user || !user.user_id) {
      toast.error("User not authenticated. Please login again.");
      return;
    }

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Failed to load Razorpay SDK.");
        return;
      }

      const amountInPaise = PLAN_AMOUNTS[plan] * 100;
      const name = user?.name || "Guest User";
      const email = user?.email || "void@example.com";
      const contact = contactNum || user?.contact || "9999999999";

      const createRes = await fetch(`${BACKEND_BASE_URL}/reportpayment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: amountInPaise,
          plan: activePlan,
          currency: "INR",
          customer: { name, email, contact },
        }),
      });

      const createData = await createRes.json();
      if (!createRes.ok || !createData.success) {
        showToast(createData.message || "Unable to create order.", "error");
        return;
      }

      const { order_id, key_id, amount, currency } = createData;

      const options = {
        key: key_id,
        amount,
        currency,
        name: "MARSMATE AI",
        description: `${plan} Validation Report`,
        image: "https://i.postimg.cc/mr6Mdf1P/Payment-logo-(1).png",
        order_id,
        prefill: { name, email, contact },
        theme: { color: plan === "Premium" ? "#ffd700" : "#c0c0c0" },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${BACKEND_BASE_URL}/reportpayment/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                reportId,
                reportType,
                plan: activePlan,
                customer_name: name,
                customer_email: email,
                customer_contact: contact,
                mindReports:false,
              }),
            });

            const verifyData = await verifyRes.json();

            if (!verifyRes.ok || !verifyData.success) {
              showToast(verifyData?.message || "Verification failed", "error");
              return;
            }

            await sendSelectedReport(reportId, user.user_id, verifyData.invoice_id);
          } catch (err) {
            console.error("Verification error:", err);
            showToast("Payment verification failed.", "error");
          }
        },
        modal: {
          ondismiss: () => toast.info("Payment was cancelled."),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("Unable to start payment.");
    }
  };

  const handleProClick = () => startReportPayment("Pro");
  const handlePremiumClick = () => startReportPayment("Premium");

  const getFeatureIcon = (text) => {
    const t = text.toLowerCase();
    if (t.includes("exclusive feedback")) return <FaCommentDots className="triple-payment-gateway-icon" />;
    if (t.includes("section score")) return <FaChartPie className="triple-payment-gateway-icon" />;
    if (t.includes("executive summary")) return <FaFileAlt className="triple-payment-gateway-icon" />;
    if (t.includes("score")) return <FaChartBar className="triple-payment-gateway-icon" />;
    if (t.includes("summary")) return <FaListAlt className="triple-payment-gateway-icon" />;
    if (t.includes("feedback")) return <FaComments className="triple-payment-gateway-icon" />;
    if (t.includes("customized")) return <FaTasks className="triple-payment-gateway-icon" />;
    if (t.includes("remarks")) return <FaClipboardCheck className="triple-payment-gateway-icon" />;
    if (t.includes("lacking")) return <FaBrain className="triple-payment-gateway-icon" />;
    if (t.includes("grades")) return <FaStar className="triple-payment-gateway-icon" />;
    if (t.includes("id")) return <FaIdBadge className="triple-payment-gateway-icon" />;
    if (t.includes("download")) return <FaDownload className="triple-payment-gateway-icon" />;
    return <FaCheck className="triple-payment-gateway-icon" />;
  };

  const getCrownColor = (title) => {
    if (title === "Pro") return "#c0c0c0";
    if (title === "Premium") return "#ffd700";
    return "#000";
  };

  const plans = [
    {
      title: "Basic",
      price: "10",
      subtitle: "Unlock Scorecard access",
      features: [
        "Overall Idea Score out of 600",
        "Risk level assessment",
        "Basic startup validation summary",
        "Unlock full detailed report",
        "Download report for pitch decks",
      ],
      onClick: handleInitialPayment,
      isUnlock: true,
    },
    {
      title: "Pro",
      price: "199",
      subtitle: "Full Report Card",
      features: [
        "Your Score",
        "Your Validation Report",
        "Customized Plans",
        "Remarks & Recommended Actions",
        "Splitting and showing where you are lacking",
        "Download Feature",
      ],
      onClick: handleProClick,
      disabled: false,
    },
    {
      title: "Premium",
      price: "299",
      subtitle: "Advanced Report Card",
      features: [
        "Your Score",
        "Exclusive Feedback Based on Score",
        "Specific Section Score and Their Grades with Key Observations",
        "Special Executive Summary Based on Score",
        "Customized Plans",
        "Downloadable Option",
      ],
      onClick: handlePremiumClick,
      disabled: false,
    },
  ];

  if (!reportId) {
    return (
      <div className="triple-payment-gateway-wrapper">
        <Loader text="Redirecting..." />
      </div>
    );
  }

  return (
    <div className="triple-payment-gateway-wrapper">
      {loading ? (
        <Loader text="Please wait, we are sending your report to your email..." />
      ) : (
        <>
          <h2 className="triple-payment-gateway-heading">Choose your plan</h2>

          <div className="triple-payment-gateway-cards">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`triple-payment-gateway-card ${plan.title.toLowerCase().replace(/\s+/g, "-")} ${plan.isUnlock ? "unlock" : ""}`}
              >
                <div className="triple-payment-gateway-title-row">
                  <h3 className="triple-payment-gateway-title">{plan.title}</h3>
                  <FaCrown
                    className="triple-payment-gateway-crown"
                    style={{ color: getCrownColor(plan.title) }}
                  />
                </div>

                <div className="triple-payment-gateway-price-box">
                  <span className="triple-payment-gateway-price">
                    ₹
                    {(plan.isUnlock ? [1, 0] : (activePlan === plan.title ? animatedDigits[plan.title] : plan.price.split("").map(Number))).map(
                      (digit, idx) => (
                        <span key={idx} className="triple-payment-gateway-digit-container">
                          <span
                            className="triple-payment-gateway-digit-strip"
                            style={{ transform: `translateY(-${digit * 48}px)` }}
                          >
                            {[...Array(10)].map((_, n) => (
                              <span key={n} className="triple-payment-gateway-digit">
                                {n}
                              </span>
                            ))}
                          </span>
                        </span>
                      )
                    )}
                  </span>
                </div>

                <p className="triple-payment-gateway-subtitle">{plan.subtitle}</p>

                <button
                  className={`triple-payment-gateway-btn ${plan.disabled ? "triple-payment-gateway-disabled" : ""}`}
                  onClick={plan.onClick}
                  disabled={plan.disabled || payLoading}
                >
                  {plan.isUnlock ? (payLoading ? "Processing..." : "UNLOCK FOR ₹10") : `Get ${plan.title}`}
                </button>

                <ul className="triple-payment-gateway-features">
                  {plan.features.map((f, i) => (
                    <li key={i} className="triple-payment-gateway-feature-item">
                      {getFeatureIcon(f)}
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

               
              </div>
            ))}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnHover
            draggable
          />
        </>
      )}
    </div>
  );
}
